<!-- オーダー > 公共積替フォーム -->
<template>
  <div>
    <validation-observer ref="TransshipmentFormValidator">
      <v-form>
        <v-card flat>
          <v-toolbar  flat>
            <v-toolbar-title>{{ $route.params.id ? '公共積替編集' : '公共積替追加' }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{ required: true, alpha_num_only: true, min: 4, max: 10 }"
                  name="事業場コード"
                  class="requiredMark"
                >
                  <v-text-field
                    v-model="public_place.code"
                    :error-messages="errors"
                    :disabled="!!$route.params.id"
                    dense
                    hide-details="auto"
                    label="事業場コード"
                    outlined
                    persistent-placeholder
                    placeholder="例）K001"
                  >
                    <template v-slot:label>
                      <span>事業場コード</span>
                      <span class="ml-1">
                        ({{ public_place.code?.length || 0 }} / 10)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                最近使用したコード:&nbsp;<strong>{{ public_place.recent_registered_codes }}</strong>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  class="requiredMark"
                  name="事業場名"
                  rules="required|max:60"
                >
                  <v-text-field
                    v-model="public_place.name"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="事業場名"
                    outlined
                    persistent-placeholder
                    placeholder="例）△△△△港"
                  >
                    <template v-slot:label>
                      <span>事業場名</span>
                      <span class="ml-1">
                        ({{ public_place.name?.length || 0 }} / 60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>

            <!-- 郵便番号 -->
            <v-row>
              <v-col cols="12" md="5">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{zip_code: true, required: true, max:8 }"
                  name="郵便番号"
                  class="requiredMark"
                >
                  <v-text-field
                    v-model="public_place.zip_code"
                    :error-messages="errors"
                    dense
                    label="郵便番号"
                    hide-details="auto"
                    hint="ハイフンを含めて入力してください"
                    outlined
                    persistent-hint
                    persistent-placeholder
                    placeholder="例）111-1111"
                    required
                  >
                    <template v-slot:label>
                      <span>郵便番号</span>
                      <span class="ml-1">
                        ({{ public_place.zip_code?.length || 0 }} / 8)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                    <template v-slot:append-outer>
                      <zip-code-search
                        :zip-code="public_place.zip_code"
                        @zipAddress="setZipAddress"
                      />
                      &nbsp;
                      <v-btn
                        outlined
                        small
                        @click="searchZipButtonClickEvent"
                      >
                        住所→郵便番号
                      </v-btn>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /郵便番号 -->

            <!-- 都道府県／市区町村 -->
            <v-row>
              <v-col cols="12" md="3">
                <validation-provider
                  v-slot="{ errors }"
                  name="都道府県"
                  rules="required"
                  class="requiredMark"
                >
                  <v-autocomplete
                    v-model="public_place.prefecture_code"
                    :error-messages="errors"
                    :items="prefList"
                    dense
                    hide-details="auto"
                    item-text="text"
                    item-value="value"
                    label="都道府県"
                    outlined
                    persistent-placeholder
                    placeholder="例）東京都"
                  />
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="市区町村"
                  rules="required|full_width|max:20"
                  class="requiredMark"
                >
                  <v-text-field
                    v-model="public_place.address_1"
                    :error-messages="errors"
                    dense
                    label="市区町村"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）中央区"
                  >
                    <template v-slot:label>
                      <span>市区町村</span>
                      <span class="ml-1">
                        ({{ public_place.address_1?.length || 0 }} / 20)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /都道府県／市区町村 -->

            <!-- 町域 -->
            <v-row>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="町域"
                  rules="full_width|max:40"
                >
                  <v-text-field
                    v-model="public_place.address_2"
                    :error-messages="errors"
                    dense
                    label="町域"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）日本橋兜町"
                  >
                    <template v-slot:label>
                      <span>町域</span>
                      <span class="ml-1">
                        ({{ public_place.address_2?.length || 0 }} / 40)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /町域 -->

            <!-- 詳細住所 -->
            <v-row>
              <v-col md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="詳細住所"
                  rules="max:50"
                >
                  <v-text-field
                    v-model="public_place.address_3"
                    :error-messages="errors"
                    dense
                    label="詳細住所"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）１－２－３"
                  >
                    <template v-slot:label>
                      <span>詳細住所</span>
                      <span class="ml-1">
                        ({{ public_place.address_3?.length || 0 }} / 50)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /詳細住所 -->

            <!-- 電話番号 -->
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="電話番号"
                  :rules="{ numeric_hyphen_only: true,min: 2, max: 15 }"
                >
                  <v-text-field
                    v-model="public_place.phone_number"
                    :error-messages="errors"
                    dense
                    hint="電話番号を「-」ハイフン「( )」込みで入力します"
                    persistent-hint
                    label="電話番号"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）000-0000-0000"
                  >
                    <template v-slot:label>
                      <span>電話番号</span>
                      <span class="ml-1">
                        ({{ public_place.phone_number?.length || 0 }} / 15)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /電話番号 -->
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              @click="cancelButtonClickEvent"
            >
              閉じる
            </v-btn>
            <v-btn
              color="primary"
              @click="registerButtonClickEvent"
            >
              登録
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </validation-observer>
    <search-zip-modal
      :enable="enableSearchZipModal"
      :initial-prefecture-code="public_place.prefecture_code"
      :initial-city-name="public_place.address_1"
      :initial-town-name="public_place.address_2"
      @reflectFullCallback="searchZipReflectCallback"
      @reflectZipCallback="searchZipReflectZipCallback"
      @closeCallback="closeModalCallback"
    />
  </div>
</template>

<script>

import {mapActions} from 'vuex'
import PrefectureList from '../../../../mixins/PrefectureList'
import ZipCodeSearch from '../../../../components/ZipCodeSearch'
import SearchZipModal from "../../../components/SearchZipModal.vue";

export default {
  name: 'TransshipmentForm',
  components: {
    ZipCodeSearch,
    SearchZipModal,
  },
  mixins: [ PrefectureList ],
  data () {
    return {
      public_place: {
        code: null,
        name: null,
        zip_code: null,
        prefecture_code: null,
        address_1: null,
        address_2: null,
        address_3: null,
        phone_number: null,
      },
      enableSearchZipModal: false,
    }
  },
  created() {
    if (this.$route.params.id) {
      this.editMasterPublicPlace({ id: this.$route.params.id }).then((content) => {
        this.public_place = content
      })
    } else {
      this.newMasterPublicPlace().then((content) => {
        this.public_place = content
      })
    }
  },
  methods: {
    ...mapActions('DriverMasterPublicPlace', {
      newMasterPublicPlace: 'new',
      editMasterPublicPlace: 'edit',
      createMasterPublicPlace: 'create',
      updateMasterPublicPlace: 'update'
    }),
    searchZipButtonClickEvent () {
      this.enableSearchZipModal = true;
    },
    registerButtonClickEvent () {
      let $self = this
      this.$refs.TransshipmentFormValidator.validate().then((result) => {
        if(result){
          $self.sendRegistrationInformation()
        }
      })
    },
    sendRegistrationInformation(){
      const parameters = {
        code: this.public_place.code,
        name: this.public_place.name,
        zip_code: this.public_place.zip_code,
        prefecture_code: this.public_place.prefecture_code,
        address_1: this.public_place.address_1,
        address_2: this.public_place.address_2,
        address_3: this.public_place.address_3,
        phone_number: this.public_place.phone_number,
      }

      if (this.public_place.id) {
        this.updateMasterPublicPlace({ id: this.public_place.id, params: parameters }).then(() => {
          this.$router.push( { name: 'DriverMasterPublicPlaces' } )
        })
      } else {
        this.createMasterPublicPlace({ params: parameters }).then(() => {
          this.$router.push( { name: 'DriverMasterPublicPlaces', params: { resetPagination: true } } )
        })
      }
    },
    cancelButtonClickEvent: function() {
      this.$router.push({ name: 'DriverMasterPublicPlaces' })
    },
    setZipAddress (content) {
      this.public_place.prefecture_code = `${content.pref_code}`
      this.public_place.address_1 = `${content.locality}`
      this.public_place.address_2 = `${content.street}`
    },
    closeModalCallback () {
      this.enableSearchZipModal = false;
    },
    /**
     * 郵便番号検索反映ボタン コールバック
     * @param address { Object }
     * @property address.zip_code { string } 郵便番号
     * @property address.prefecture_code { number } 都道府県コード
     * @property address.city { string } 市区町村
     * @property address.town { string } 町域
     */
    searchZipReflectCallback (address) {
      this.public_place.zip_code = address.zip_code
      this.public_place.prefecture_code = `${address.prefecture_code}`
      this.public_place.address_1 = address.city
      this.public_place.address_2 = address.town
    },
    searchZipReflectZipCallback (zipCode) {
      this.public_place.zip_code = zipCode;
    },
  }
}
</script>
